import CircleIcon from "@mui/icons-material/Circle";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";

import { NotificationSnackbar } from "../../../../components/ui/NotificationSnackbar";
import { logError } from "../../../../utils/error";
import { useAuth } from "../../../auth/AuthContext";
import { DEFAULT_LOCALE, DEFAULT_TIMEZONE } from "../../constants";
import { useNotification } from "../../hooks/useNotification";
import { useAccountAnalysisStore } from "../../store/accountAnalysisStore";
import { AccountAnalysisConfiguration, Bcp47Locale, IanaTimezone } from "../../types";
import { LocaleSelector } from "./LocaleSelector";
import { TimezoneSelector } from "./TimezoneSelector";

interface BulletItemProps {
  text: string;
  isSubItem?: boolean;
}

const BulletItem: React.FC<BulletItemProps> = ({ text, isSubItem = false }) => (
  <ListItem sx={{ py: isSubItem ? 0 : 0.5, pl: isSubItem ? 4 : 0 }}>
    <ListItemIcon sx={{ minWidth: 28 }}>
      {isSubItem ? (
        <CircleIcon sx={{ fontSize: 6, color: "text.secondary" }} />
      ) : (
        <FiberManualRecordIcon sx={{ fontSize: 8 }} />
      )}
    </ListItemIcon>
    <ListItemText>
      <Typography
        variant="body2"
        color={isSubItem ? "text.secondary" : undefined}
      >
        {text}
      </Typography>
    </ListItemText>
  </ListItem>
);

const AllConversationsDescription: React.FC = () => (
  <Box>
    <List dense disablePadding>
      <BulletItem text="Every conversation in the organization will be analyzed, including those from people not in the People list." />
      <BulletItem text="People in the people list will use their assigned analysis group lenses." isSubItem />
      <BulletItem text="People in the people list not assigned to a group will use industry default lenses." isSubItem />
      <BulletItem text="Other people in the organization not in the people list will use industry default lenses." isSubItem />
      <BulletItem text="You can add exclusions by adding people in the people list and setting them to inactive." />
      <BulletItem text="You can add exclusions by deactivating a group." />
    </List>
  </Box>
);

const PeopleOnlyDescription: React.FC = () => (
  <Box>
    <List dense disablePadding>
      <BulletItem text="Only analyze conversations for people in the people list." />
      <BulletItem text="People in the people list will use their assigned analysis group lenses." isSubItem />
      <BulletItem text="People in the people list not assigned to a group will use industry default lenses." isSubItem />
      <BulletItem text="All other people's conversations will be ignored." />
    </List>
  </Box>
);

const PausedDescription: React.FC = () => (
  <Box>
    <List dense disablePadding>
      <BulletItem text="Stop analyzing all conversations." />
      <BulletItem text="No new analysis will be performed until this setting is changed." />
      <BulletItem text="Useful for maintenance or system updates." />
    </List>
  </Box>
);

const scopeDescriptions: Record<AccountAnalysisConfiguration["analysis_scope"], React.ReactNode> = {
  all_conversations: <AllConversationsDescription />,
  people_only: <PeopleOnlyDescription />,
  paused: <PausedDescription />
};

export const AccountConfiguration: React.FC = () => {
  const { tenantId } = useAuth();
  const {
    configuration,
    createConfiguration,
    updateConfiguration
  } = useAccountAnalysisStore();

  const [analysis_scope, setAnalysisScope] = useState<
    AccountAnalysisConfiguration["analysis_scope"]
  >("people_only");
  const [timezone, setTimezone] = useState<IanaTimezone>(DEFAULT_TIMEZONE);
  const [locale, setLocale] = useState<Bcp47Locale>(DEFAULT_LOCALE);
  const [hasChanges, setHasChanges] = useState(false);
  const { notification, showNotification, hideNotification } = useNotification();

  useEffect(() => {
    if (!configuration) {
      return;
    }

    setAnalysisScope(configuration.analysis_scope);

    if (configuration.timezone) {
      setTimezone(configuration.timezone);
    }

    if (configuration.locale) {
      setLocale(configuration.locale);
    }
  }, [configuration]);

  const handleScopeChange = (event: SelectChangeEvent) => {
    const newScope = event.target
      .value as AccountAnalysisConfiguration["analysis_scope"];
    setAnalysisScope(newScope);
    setHasChanges(true);
  };

  const handleTimezoneChange = (newTimezone: IanaTimezone) => {
    setTimezone(newTimezone);
    setHasChanges(true);
  };

  const handleLocaleChange = (newLocale: Bcp47Locale) => {
    setLocale(newLocale);
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      const saveOp = configuration
        ? updateConfiguration({ analysis_scope, timezone, locale })
        : createConfiguration({
          tenant_id: tenantId || "default-tenant",
          analysis_scope,
          timezone,
          locale
        });
      await saveOp;

      setHasChanges(false);
      showNotification("Configuration saved successfully!", "success");
    } catch (error) {
      logError("Failed to save configuration", error);
      showNotification("Failed to save configuration. Please try again.", "error");
    }
  };

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          Analysis Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure which conversations should be analyzed by the Enlighten360
          system
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Analysis Scope
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Define which conversations should be analyzed
          </Typography>
        </Box>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Which calls to analyze</InputLabel>
          <Select
            value={analysis_scope}
            onChange={handleScopeChange}
            label="Which calls to analyze"
            data-testid="analysis-scope-select"
          >
            <MenuItem value="all_conversations">
              Analyze all conversations
            </MenuItem>
            <MenuItem value="people_only">Restrict to people list</MenuItem>
            <MenuItem value="paused">Pause all analysis</MenuItem>
          </Select>
        </FormControl>

        <Alert
          severity="info"
          sx={{ mb: 3 }}
          icon={
            analysis_scope === "paused" ? (
              <Typography>⚠️</Typography>
            ) : undefined
          }
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
              {analysis_scope === "all_conversations" &&
                "Analyze all conversations:"}
              {analysis_scope === "people_only" && "Restrict to people list:"}
              {analysis_scope === "paused" && "Pause all analysis:"}
            </Typography>
            {scopeDescriptions[analysis_scope]}
          </Box>
        </Alert>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Regional Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Configure timezone and locale for content analysis processing
          </Typography>
        </Box>

        <Stack spacing={3}>
          <TimezoneSelector
            value={timezone}
            onChange={handleTimezoneChange}
          />
          <LocaleSelector
            value={locale}
            onChange={handleLocaleChange}
          />
        </Stack>
      </Paper>

      <Box
        display="flex"
        justifyContent="flex-end"
        mb={3}
      >
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!hasChanges}
        >
          Save Changes
        </Button>
      </Box>

      <NotificationSnackbar
        {...notification}
        onClose={hideNotification}
      />
    </Box>
  );
};
